<template>
  <div class="dealer-profile-wrapper">
    <div class="d-flex justify-content-between align-items-center">
      <h4>Dealer Profile</h4>
      <h5><span v-if="!adminView" :class="['badge', dealerStatusStyle ]">{{ dealerStatus }}</span></h5>
      <BasicDropdown v-if="adminView" :key="dealer.DealerStatus" :items="dealerStatusOptions"  v-model="dealer.DealerStatus" />
    </div>
    <hr> 
    <fieldset >
      <div class="dealer-profile">
        <div class="dealer-profile__card card card-1 shadow-sm">
          <fieldset :disabled="!can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">General Information</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Company Name" label="Company Name" v-model="dealer.CompanyName" :disabled="adminView"/>
                  </div>
                  <div class="col">
                    <BasicTextInput placeholder="Company Code" label="Company Code" v-model="dealer.StoreCode" :disabled="!adminView" />
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <DatePicker label="Effective From" :read-only="!adminView" v-model="dealer.EffectiveFrom"/>
                  </div>
                  <div class="col">
                    <DatePicker label="Effective To"  :read-only="!adminView" v-model="dealer.EffectiveTo"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Website" label="Website" v-model="dealer.Website" :disabled="adminView"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Address Line 1" label="Address Line 1" v-model="dealer.AddressLine1" :disabled="adminView"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Suite" label="Suite" v-model="dealer.AddressLine2" :disabled="adminView"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="City" label="City" v-model="dealer.City" :disabled="adminView"/>
                  </div>
                  <div class="col">
                    <StateSelect placeholder="" label="State/Province" v-model="dealer.StateId" :disabled="adminView"/>
                  </div>
                  <div class="col">
                    <ZipCodeInput placeholder="Zip" label="Zip" v-model="dealer.ZipCode" :disabled="adminView"/>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-2 shadow-sm">
          <fieldset :disabled="!can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Worksheet Defaults</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <PercentInput placeholder="7.00%" label="Sales Tax" v-model="dealer.SalesTax"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Round Customer Cost" v-model="dealer.RoundToNearestDollar"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <DefaultFreightInput placeholder="7.00%" @isDefaultFreightPercentChanged="isDefaultFreightPercentChanged" :percentInput="dealer.IsDefaultFreightPercent" v-model="dealer.DefaultFreight"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Tax Freight" v-model="dealer.TaxFreight"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-6">
                    <PercentInput placeholder="7.00%" label="GP" v-model="dealer.GPPercent"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <PercentInput placeholder="10.00%" label="Default Deposit %" v-model="dealer.DefaultDeposit"/>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-3 shadow-sm">
          <fieldset :disabled="adminView || !can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Configuration</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="OrderLink Enabled" v-model="dealer.OrderLinkEnabled"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Updates To Customers Allowed" v-model="dealer.UpdatesToCustomersAllowed"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Sales Rep Can See All Orders" v-model="dealer.SalesRepCanSeeAllOrders"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Show Order Types" v-model="dealer.ShowOrderTypes"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Allow Free Entry Add'l Sales Reps" v-model="dealer.AdditionalRepsAlternateDisplay"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Show Additional Rep 1" v-model="dealer.ShowAdditionalRep1"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Sales Reps Share Customers" v-model="dealer.SalesRepShareCustomers"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Show Additional Rep 2" v-model="dealer.ShowAdditionalRep2"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Customer Type Field 1" v-model="dealer.EnableCustomerType1"/>
                  </div>
                  <div class="col">
                    <input v-model="dealer.CustomerType1Label" label="" class="customer-type-input" :disabled="!dealer.EnableCustomerType1"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Customer Type Field 2" v-model="dealer.EnableCustomerType2"/>
                  </div>
                  <div class="col">
                    <input v-model="dealer.CustomerType2Label" label="" class="customer-type-input" :disabled="!dealer.EnableCustomerType2"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Customer Type Field 3" v-model="dealer.EnableCustomerType3"/>
                  </div>
                  <div class="col">
                    <input v-model="dealer.CustomerType3Label" label="" class="customer-type-input" :disabled="!dealer.EnableCustomerType3"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Allow Quote Pricing" v-model="dealer.AllowQuotePricing"/>
                  </div>
                  <div class="col">
                    &nbsp;
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-4 shadow-sm">
          <fieldset :disabled="!adminView">
            <div class="card-header">
              <h5 class="card-title">Primary Contact</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row">
                  <!-- <div class="col">
                    <b>Full Name:</b>
                  </div>
                  <div class="col right-justify-col">{{ dealer.FirstName }} {{ dealer.LastName }}</div> -->
                  <div class="col">
                    <BasicTextInput placeholder="" label="First Name" v-model="dealer.FirstName"/>
                  </div>
                  <div class="col">
                    <BasicTextInput placeholder="" label="Last Name" v-model="dealer.LastName"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <BasicTextInput placeholder="" label="Email Address" v-model="dealer.Email"/>
                  </div>
                  <!-- <div class="col">
                    <b>Email Address:</b>
                  </div>
                  <div class="col right-justify-col">{{ dealer.Email }}</div> -->
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-5 shadow-sm">
          <div class="card-header">
            <h5 class="card-title">Manufacturers</h5>
            <hr>
          </div>
          <div class="card-body">
            <div class="card list-display-card p-0 m-0" style="overflow-y: scroll; -webkit-overflow-scrolling: touch;">
              <div v-for="(manufacturer,index) in dealer.Manufacturers" :key="index" class="row my-2">
                <div class="col list-item">
                  <i class="fa fa-circle" aria-hidden="true"></i><b class="spacing-left">{{manufacturer}}</b>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="dealer-profile__card card card-6 shadow-sm">
          <fieldset :disabled="false">
            <div class="card-header">
              <h5 class="card-title">Additional Sales Rep Config</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row">
                  <div class="col-9">
                    <BasicTextInput placeholder="" label="Additional Rep 1 Type" v-model="newRepOneType"/>
                  </div>
                  <div class="col-3 btn-align">
                    <div class="btn btn-primary" @click="addTypeToRepOneTypes">Add</div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <ul :key="typesKey">
                      <li v-for="(type, index) in dealer.AdditionalRep1Types" :key="index">
                        <div class="type-wrapper">
                          <div >{{type}}</div>
                          <div class="delete-type delete-btn" @click="deleteAdditionalRep1Type(index)">x</div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="row">
                  <div class="col-9">
                    <BasicTextInput placeholder="" label="Additional Rep 2 Type" v-model="newRepTwoType"/>
                  </div>
                  <div class="col-3 btn-align">
                    <div class="btn btn-primary" @click="addTypeToRepTwoTypes">Add</div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <ul :key="typesKey + 1">
                      <li v-for="(type, index) in dealer.AdditionalRep2Types" :key="index">
                        <div class="type-wrapper">
                          <div >{{type}}</div>
                          <div class="delete-type delete-btn" @click="deleteAdditionalRep2Type(index)">x</div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="row">
                  <div class="col-9">
                    <BasicTextInput placeholder="" label="Sales Rep Type" v-model="newSalesRepType"/>
                  </div>
                  <div class="col-3 btn-align">
                    <div class="btn btn-primary" @click="addTypeToSalesRepTypes">Add</div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <ul :key="typesKey+2">
                      <li v-for="(type, index) in dealer.SalesRepTypes" :key="index">
                        <div class="type-wrapper">
                          <div >{{type}}</div>
                          <div class="delete-type delete-btn" @click="deleteSalesRepType(index)">x</div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-7 shadow-sm ">
          <fieldset :disabled="!can('edit-dealer-profile') && !can('upload-configurators')">
							<div class="card-header">
              <h5 class="card-title">Upload Configurator</h5>
              <hr>
            </div>
            <input :key="configFileUpdateKey" :disabled="false" type="file" class="btn m-2" accept=".zip, .tsv" @change="configuratorFileSelected">
            <div class="btn-wrapper">
              <button :disabled="false" type="button" @click="uploadConfiguratorDocument" class="btn btn-primary">Upload</button>
            </div>
          </fieldset> 
        </div>
      </div>
    </fieldset>
      <div v-if="adminView" class="dealer-profile__card card card-8 shadow-sm m-2">
        <div class="card-header">
          <h5 class="card-title">Upload Form Templates</h5>
          <hr>
        </div>
        <input :key="fileUpdateKey" :disabled="false" type="file" class="btn m-2" accept=".pdf" @change="fileSelected">
        <div class="btn-wrapper">
          <button :disabled="false" type="button" @click="uploadDocument" class="btn btn-primary">Upload</button>
        </div>
      </div>
    <!-- Save button for admin view -->
    <div v-if="adminView" class="btn-wrapper" style="text-align: right; margin-top: 20px;">
      <div @click="saveDealer" class="btn btn-primary">Save</div>
    </div>
  </div>
  
</template>

<script>
import { mapState } from 'vuex';
import { DealerService } from '@/services';
import DefaultFreightInput from '../../../components/inputComponents/DefaultFreightInput';

import {
  getKeyByValue,
  dealerStatusEnum
} from "../../../modules/helpers";

export default {
  name: 'DealerProfile',
  components: {
    DefaultFreightInput
  },
  data: () => {
    return {
      dealer: {},
      file: null,
      newRepOneType: "",
      newRepTwoType: "",
      newSalesRepType: "",
      typesKey: 100,
      fileUpdateKey: 0,
      configuratorFile: null,
      configFileUpdateKey: 0,
      dealerStatusOptions: [{text: 'Unregistered', value: 1}, {text: 'Registered', value: 2}, {text: 'Discontinued', value: 3}]
    }
  },
  computed:{
    ...mapState(['currentUser']),
    dealerStatus(){
      return getKeyByValue(dealerStatusEnum, this.dealer.DealerStatus);
    },
    dealerStatusStyle(){
      if (this.dealerStatus === 'Registered'  ) return 'badge-success';
      if (this.dealerStatus === 'Unregistered') return 'badge-warning';
      if (this.dealerStatus === 'Discontinued') return 'badge-danger';
    },
    adminView(){
      return this.$route.name === 'DealerProfile'
    }
  },
  methods: {
    deleteAdditionalRep1Type(index){
      console.log('index', index)
      this.dealer.AdditionalRep1Types.splice(index, 1)
    },
    deleteAdditionalRep2Type(index){
      console.log('index', index)
      this.dealer.AdditionalRep2Types.splice(index, 1)
    },
    deleteSalesRepType(index){
      console.log('index', index)
      this.dealer.SalesRepTypes.splice(index, 1)
    },
    addTypeToRepOneTypes(){
      this.dealer.AdditionalRep1Types.push(this.newRepOneType);
      this.newRepOneType = "";
      this.typesKey++;
    },
    addTypeToRepTwoTypes(){
      this.dealer.AdditionalRep2Types.push(this.newRepTwoType);
      this.newRepTwoType = "";
      this.typesKey++;
    },
    addTypeToSalesRepTypes(){
      this.dealer.SalesRepTypes.push(this.newSalesRepType);
      this.newSalesRepType = "";
      this.typesKey++;
    },
    uploadDocument(){
      DealerService.uploadTemplate({file: this.file, id: this.$route.params.id})
        .then(() => {
          this.file = null;
          this.fileUpdateKey++;
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Document Uploaded', type: 'success' });
        })
        .catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    uploadConfiguratorDocument(){
      DealerService.uploadConfigurator({file: this.configuratorFile})
        .then(() => {
          this.configuratorFile = null;
          this.configFileUpdateKey++;
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Document Uploaded', type: 'success' });
        })
        .catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    fileSelected(event){
      this.file = event.target.files[0]
    },
    configuratorFileSelected(event){
    
      this.configuratorFile = event.target.files[0]
    },
    isDefaultFreightPercentChanged(isDefPercent){
      this.dealer.IsDefaultFreightPercent = isDefPercent;
    },
    saveDealer(){
      DealerService.update(this.dealer.Id, this.dealer).then(res => {
          this.getDealer();
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Dealer Updated', type: 'success' });
        }).catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    getDealer(){
      console.log('route', this.$route)
      let dealerId = this.adminView ? this.$route.params.id : this.currentUser.DealerId;
      // this.readOnly = 
      DealerService.get(dealerId).then(res => {
        this.dealer = res;
        this.dealer.AdditionalRep1Types = this.dealer.AdditionalRep1Types || [];
        this.dealer.AdditionalRep2Types = this.dealer.AdditionalRep2Types || [];
        this.dealer.SalesRepTypes = this.dealer.SalesRepTypes || []
      }).catch(err => {
        this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
      })
    }
  },

  created(){
    this.getDealer();
  }

}
</script>

<style scoped lang="scss">
  // .list-div{
  //   font-size: .5em;
  // }
  .dealer-profile-wrapper{
    // max-width: 1800px;
    min-height: 100%;
    .dealer-profile{
      
      .card-1 {
        grid-area: card-1;
      }
      .card-2 {
        grid-area: card-2;
      }
      .card-3 {
        grid-area: card-3;
      }
      .card-4 {
        grid-area: card-4;
      }
      .card-5 {
        grid-area: card-5;
      }
      .card-6{
        grid-area: card-6;
      }
      .card-7{
        grid-area: card-7;
      }
      .card-8{
        grid-area: card-8;
      }
      height: 90%;
      display: grid;
      grid-template-columns: 50% 50%;
      grid-template-rows: auto;
      grid-template-areas: 
        "card-1 card-2"
        "card-1 card-2"
        "card-1 card-3"
        "card-4 card-5"
        "card-4 card-5"
        "card-6 card-7"
        "card-8 card-8";
      &__card{
        margin: 10px;
      }
    }
    .right-justify-col {
      display: flex;
      justify-content: flex-end;
    }
    .list-display-card {
      background-color: white;
      height: 100%;
      max-height: 200px;
      overflow: scroll;
      i {
        font-size: 12px;
      }
      .list-item {
        display: flex;
        font-size: .6em;
        justify-content: flex-start;
        align-items: center;
        margin-left: 10px;
        .spacing-left {
          margin-left: 10px;
        }
      }
    }
    .btn-align{
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
    }
    .delete-type{
      color: red;
    }
    .btn-wrapper{
      width: 100%;
      padding: 10px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .btn {
        margin: 5px;
      }
    }
    .type-wrapper{
      display: flex;
      justify-content: flex-start;
      align-items: center;
      .delete-btn{
        margin-left: 5px;
        cursor: pointer;
      }
    }
    .customer-type-input {
      width: 150px;
      margin: 0;
      border-radius: 4px;
      border: 1px solid rgb(150, 150, 150);
      padding: 1px 5px;
      color: rgb(73, 80, 87);
      font-size: 16px;
      font-weight: 300;
    }
  }
</style>
